/**
 * The 1v1 room, client side.
 *
 * Wraps the Worker's `/r/*` routes and holds the room view the screens read.
 * Every rule is enforced on the server — this cannot be the place that decides
 * whose innings it is, because the whole point of the design is that neither
 * client is trusted.
 *
 * ## Polling
 *
 * Async 1v1 means waiting for the opponent, and the only way to learn they have
 * played is to ask. Durable Objects bill per request *and* per active duration,
 * so a tight poll is the one thing in this design that could actually cost
 * money. Hence: poll only while genuinely waiting, back off as the wait grows,
 * and stop entirely when the tab is hidden.
 *
 * Web Push replaces most of this later (the plan's Phase 8 note). Until then,
 * backoff is what keeps an abandoned room from costing anything.
 */

import { deviceId } from './device';
import { getToken } from './turnstile';
import type { DraftPlayer } from '@ipl-sim/engine-cricket';
import type { Answer } from '@ipl-sim/engine-cricket';

const base = import.meta.env.VITE_ROOM_ENDPOINT ?? '';

export type Side = 'host' | 'guest';
export type RoomStatus =
  | 'WAITING'
  | 'DRAFTING'
  | 'INNINGS_1'
  | 'INNINGS_2'
  | 'SUPER_OVER'
  | 'DONE'
  | 'EXPIRED';

export interface RoomInnings {
  readonly side: Side;
  readonly runs: number;
  readonly wickets: number;
  readonly legalBalls: number;
  readonly decisions: readonly Answer[];
  readonly coefficientVersion: number;
}

export interface RoomView {
  readonly code: string;
  readonly seed: string;
  readonly status: RoomStatus;
  readonly you: Side | null;
  readonly expiresAt: number;
  readonly extended: boolean;
  readonly respinAllowance: number;
  readonly battingFirst?: Side;
  readonly awaiting: Side | null;
  readonly target: number | null;
  readonly winner?: Side | 'TIE';
  readonly host: { teamName: string; ready: boolean };
  readonly guest: { teamName: string; ready: boolean } | null;
  readonly opponentXI: readonly DraftPlayer[] | null;
  /** Your own squad, so a reload — or another device — can carry on. */
  readonly yourXI: readonly DraftPlayer[] | null;
  readonly yourSubs: readonly DraftPlayer[] | null;
  readonly innings: readonly RoomInnings[];
  /** Which super over is being played, 0-based. Only meaningful in SUPER_OVER. */
  readonly superOverIndex: number;
  /** The seed for the super over in progress; each round has its own. */
  readonly superOverSeed: string | null;
  readonly superOvers: readonly { side: Side; runs: number; wickets: number; legalBalls: number }[];
}

/**
 * The room this device is currently in.
 *
 * Persisted because a room outlives a page: an async match can span hours, and
 * without this a reload drops the player on the home screen with no way back
 * except digging out the invite link they were sent. Only the code is stored —
 * the room itself is server-side, and the code is not a secret from its own
 * participants.
 */
const ACTIVE_KEY = 'p11.room';

export function rememberRoom(code: string): void {
  try {
    localStorage.setItem(ACTIVE_KEY, code);
  } catch {
    /* a room that cannot be remembered still plays; it just will not resume */
  }
}

export function forgetRoom(): void {
  try {
    localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* ignore */
  }
}

export function rememberedRoom(): string | null {
  try {
    const code = localStorage.getItem(ACTIVE_KEY);
    return code && /^[A-Z2-9]{6}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

export class RoomError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'RoomError';
    this.status = status;
  }
}

async function call(path: string, body?: unknown): Promise<RoomView> {
  if (!base) throw new RoomError('1v1 is not configured in this build', 0);

  const response = await fetch(`${base}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'content-type': 'application/json', 'x-device-id': deviceId() },
    body: body === undefined ? undefined : JSON.stringify(body),
    cache: 'no-store',
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new RoomError(payload.error ?? `request failed (${response.status})`, response.status);
  }
  return payload as RoomView;
}

/**
 * Creating a room is the one call that carries a challenge token: it is the
 * only unauthenticated write that allocates a Durable Object, and so the only
 * one worth protecting.
 */
export const createRoom = async (teamName: string): Promise<RoomView> =>
  call('/r/new', { teamName, turnstileToken: await getToken() });
export const joinRoom = (code: string, teamName: string): Promise<RoomView> =>
  call(`/r/${code}/join`, { teamName });
export const fetchRoom = (code: string): Promise<RoomView> => call(`/r/${code}/view`);
export const extendRoom = (code: string): Promise<RoomView> => call(`/r/${code}/extend`, {});

export const submitSquad = (
  code: string,
  xi: readonly DraftPlayer[],
  subs: readonly DraftPlayer[],
): Promise<RoomView> => call(`/r/${code}/squad`, { xi, subs });

export const submitInnings = (
  code: string,
  payload: {
    decisions: readonly Answer[];
    claimed: { runs: number; wickets: number; legalBalls: number };
    coefficientVersion: number;
  },
): Promise<RoomView> => call(`/r/${code}/innings`, payload);

/** Whether the room is waiting on somebody other than you. */
export function waitingOnOpponent(view: RoomView): boolean {
  if (view.status === 'WAITING') return true;
  if (view.status === 'DRAFTING') {
    const you = view.you === 'host' ? view.host : view.guest;
    return Boolean(you?.ready);
  }
  if (view.status === 'INNINGS_1' || view.status === 'INNINGS_2' || view.status === 'SUPER_OVER') {
    return view.awaiting !== null && view.awaiting !== view.you;
  }
  return false;
}

/**
 * Polls while the room is waiting on the opponent.
 *
 * Backoff, not a fixed interval: someone who steps away for an hour must not
 * generate 1,200 requests against an object that bills for being awake. Starts
 * responsive because the common case is a friend joining within a minute.
 */
export class RoomPoller {
  #timer: ReturnType<typeof setTimeout> | undefined;
  #delay = 3_000;
  #stopped = false;

  static readonly MIN_DELAY = 3_000;
  static readonly MAX_DELAY = 60_000;

  constructor(
    private readonly code: string,
    private readonly onUpdate: (view: RoomView) => void,
    private readonly onError?: (error: unknown) => void,
  ) {}

  start(): void {
    this.#stopped = false;
    this.#schedule();
    // A hidden tab is not being watched, so nothing it learns is worth paying
    // for. Resuming on visibility gives an immediate refresh, which is what a
    // user coming back actually wants.
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  stop(): void {
    this.#stopped = true;
    clearTimeout(this.#timer);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = (): void => {
    if (this.#stopped) return;
    if (document.visibilityState === 'hidden') {
      clearTimeout(this.#timer);
    } else {
      this.#delay = RoomPoller.MIN_DELAY;
      this.#schedule(0);
    }
  };

  #schedule(delay = this.#delay): void {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(async () => {
      if (this.#stopped) return;

      // Hidden: skip the request, but keep the chain alive at the slowest
      // cadence. Returning here instead would end the loop and leave it
      // depending entirely on a visibilitychange event firing -- and a loop
      // that only recovers via an event you cannot guarantee is a loop that
      // silently stops updating.
      if (document.visibilityState === 'hidden') {
        this.#schedule(RoomPoller.MAX_DELAY);
        return;
      }

      try {
        const view = await fetchRoom(this.code);
        this.onUpdate(view);
        // Stop as soon as the room is not waiting on anyone else: continuing to
        // poll a finished match is pure cost.
        if (!waitingOnOpponent(view)) {
          this.stop();
          return;
        }
      } catch (error) {
        this.onError?.(error);
      }
      this.#delay = Math.min(this.#delay * 1.6, RoomPoller.MAX_DELAY);
      this.#schedule();
    }, delay);
  }
}
